import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
// import 'react-quill-new/dist/quill.snow.css';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { analyzeSEO } from '../../utils/seo';
import { useNotification } from '../../context/NotificationContext';
import { 
  Save, 
  ArrowLeft, 
  Image as ImageIcon, 
  Search, 
  Loader2, 
  Lightbulb, 
  CheckCircle2, 
  AlertCircle, 
  Globe, 
  FileText,
  Eye,
  Film,
  Star
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import MediaManager from '../../components/admin/MediaManager';

const ArticleEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams(); 
  const { addNotification } = useNotification();
  const quillRef = useRef(null); // Ref to access Quill instance
  
  const [loadingData, setLoadingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title_id: '',
    slug: '',
    content_id: '',
    meta_title_id: '',
    meta_description_id: '',
    seo_focus_keyword: '',
    status: 'draft',
    is_featured: 0,
    featured_image: ''
  });

  // Media Manager State
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaConfig, setMediaConfig] = useState({ type: 'image', callback: null });

  const [seoStats, setSeoStats] = useState({ score: 0, density: 0, words: 0, issues: [] });
  const [imagePreview, setImagePreview] = useState('');

  // Fetch Data
  useEffect(() => {
    if (id) {
      setLoadingData(true);
      axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/articles.php?id=${id}`)
        .then(res => {
          if (res.data) {
            setFormData(prev => ({ ...prev, ...res.data }));
            if (res.data.featured_image) setImagePreview(res.data.featured_image);
          }
        })
        .catch(err => console.error("Failed to load article:", err))
        .finally(() => setLoadingData(false));
    }
  }, [id]);

  // Real-time SEO
  useEffect(() => {
    const stats = analyzeSEO(formData.content_id || '', formData.seo_focus_keyword || '');
    setSeoStats(stats);
  }, [formData.content_id, formData.seo_focus_keyword]);

  // Handlers
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content_id: value }));
  };

  // Open Media Manager
  const openMediaManager = (type, callback) => {
    setMediaConfig({ type, callback });
    setShowMediaModal(true);
  };

  const handleMediaSelect = (url) => {
    if (mediaConfig.callback) {
      mediaConfig.callback(url);
    }
  };

  // Quill Handlers
  const quillImageHandler = () => {
    openMediaManager('image', (url) => {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      quill.insertEmbed(range ? range.index : 0, 'image', url);
    });
  };

  const quillVideoHandler = () => {
    openMediaManager('video', (url) => {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      // Determine if it's an mp4 (needs specific handling ideally, but trying default video blot first)
      quill.insertEmbed(range ? range.index : 0, 'video', url);
    });
  };

  const handleCoverImageSelect = () => {
    openMediaManager('image', (url) => {
      setFormData(prev => ({ ...prev, featured_image: url }));
      setImagePreview(url);
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const data = new FormData();
    
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key] || '');
    });
    
    if (id) data.append('id', id);
    // Note: 'featured_image' is already in formData, so it gets sent.
    // Backend will use it if no new file is in $_FILES (which there isn't).

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/articles.php`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      addNotification('Artikel berhasil disimpan!', 'success');
      navigate('/admin/articles');
    } catch (err) {
      console.error(err);
      addNotification('Gagal menyimpan artikel.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Quill Modules - Optimized for mobile
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        ['link', 'image', 'video', 'clean']
      ],
      handlers: {
        image: quillImageHandler,
        video: quillVideoHandler
      }
    },
  }), []);

  // Styles
  const styles = {
    wrapper: {
      width: '100%', // Full width
      paddingBottom: '4rem'
    },
    headerNav: {
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    topSection: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1.5rem',
      marginBottom: '1.5rem'
    },
    bottomSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem',
      alignItems: 'start'
    },
    editorColumn: {
      flex: '1 1 65%', // Takes 65% space on desktop
      minWidth: '300px'
    },
    sidebarColumn: {
      flex: '1 1 30%', // Takes 30% space on desktop
      minWidth: '280px'
    },
    card: { 
      backgroundColor: '#fff', 
      padding: '1.5rem', 
      borderRadius: '1rem', 
      border: '1px solid #e5e7eb',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    inputGroup: { marginBottom: '1.5rem' },
    label: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      fontSize: '0.9rem', 
      fontWeight: '600', 
      marginBottom: '0.5rem', 
      color: '#374151' 
    },
    input: { 
      width: '100%', 
      padding: '0.8rem 1rem', 
      border: '1px solid #e5e7eb', 
      borderRadius: '0.5rem', 
      fontSize: '0.95rem',
      transition: 'border-color 0.2s',
      outline: 'none'
    },
    titleInput: {
      width: '100%',
      padding: '1rem',
      fontSize: '1.5rem',
      fontWeight: '700',
      border: '1px solid #e5e7eb',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      outline: 'none'
    },
    textarea: { 
      width: '100%', 
      padding: '0.8rem', 
      border: '1px solid #e5e7eb', 
      borderRadius: '0.5rem', 
      minHeight: '100px', 
      fontFamily: 'inherit',
      resize: 'vertical'
    },
    previewImg: { 
      width: '100%', 
      height: 'auto', 
      maxHeight:'200px', 
      objectFit: 'cover', 
      borderRadius: '0.5rem', 
      marginTop: '0.75rem', 
      border: '1px solid #e5e7eb' 
    },
    scoreCircle: (score) => ({
      width: '50px', height: '50px', borderRadius: '50%',
      background: score > 70 ? '#d1fae5' : score > 40 ? '#fef3c7' : '#fee2e2',
      color: score > 70 ? '#047857' : score > 40 ? '#b45309' : '#b91c1c',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '1.1rem', fontWeight: 'bold', margin: '0'
    }),
    backBtn: {
      background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:'0.5rem', 
      color:'#6b7280', fontSize: '0.95rem', padding: '0.5rem 0', fontWeight: '500'
    },
    saveBtn: {
      width: '100%', 
      padding: '0.8rem', 
      borderRadius: '0.5rem', 
      border: 'none',
      backgroundColor: '#18181b', // Zinc-900
      color: '#fff', 
      fontWeight: '600', 
      cursor: submitting ? 'not-allowed' : 'pointer',
      display: 'flex', 
      justifyContent: 'center', 
      gap: '0.5rem', 
      alignItems: 'center', 
      fontSize: '1rem',
      transition: 'all 0.2s'
    },
    tipBox: {
      display: 'flex',
      alignItems: 'start',
      gap: '0.75rem',
      padding: '1rem',
      backgroundColor: '#f0f9ff',
      borderRadius: '0.5rem',
      color: '#0369a1',
      fontSize: '0.9rem',
      marginTop: '1rem'
    }
  };

  if (loadingData) return <DashboardLayout>
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: '#6b7280'}}>
      <Loader2 className="animate-spin" size={24} style={{marginRight: '0.5rem'}}/> Memuat Editor...
    </div>
  </DashboardLayout>;

  return (
    <DashboardLayout>
      <div style={styles.wrapper}>
        <MediaManager 
          isOpen={showMediaModal} 
          onClose={() => setShowMediaModal(false)} 
          onSelect={handleMediaSelect}
          allowVideo={mediaConfig.type === 'video'}
        />

        <div style={styles.headerNav}>
          <button onClick={() => navigate('/admin/articles')} style={styles.backBtn}>
            <ArrowLeft size={18} /> Kembali
          </button>
          <div style={{fontSize: '0.9rem', color: '#6b7280'}}>
            {formData.status === 'published' ? <span style={{color:'#059669', display:'flex', alignItems:'center', gap:'0.25rem'}}><Globe size={14}/> Published</span> : 'Draft Mode'}
          </div>
        </div>

        {/* TOP SECTION: Editor + Sidebar (Publish/Cover) */}
        <div style={styles.topSection}>
          
          {/* LEFT: EDITOR */}
          <div style={styles.editorColumn}>
            <input 
              style={styles.titleInput} 
              name="title_id" 
              value={formData.title_id} 
              onChange={handleChange} 
              placeholder="Tulis Judul Artikel Disini..." 
            />

            <div style={{...styles.card, height: '600px', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
              <label style={styles.label}><FileText size={16} /> Konten Artikel</label>
              {/* Quill Wrapper */}
              <div style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}> 
                <ReactQuill 
                  ref={quillRef}
                  theme="snow" 
                  value={formData.content_id} 
                  onChange={handleContentChange} 
                  modules={modules}
                  className="quill-fixed-height"
                />
              </div>
            </div>
            
            <div style={styles.tipBox}>
              <Lightbulb size={18} style={{flexShrink: 0, marginTop: '2px'}} />
              <span>Tekan enter untuk baris baru. Gunakan toolbar diatas untuk format text dan <b>insert gambar</b>.</span>
            </div>
          </div>

          {/* RIGHT: PUBLISH & IMAGE */}
          <div style={styles.sidebarColumn}>
            
            {/* ACTION CARD */}
            <div style={styles.card}>
              <h3 style={{fontWeight: '700', marginBottom: '1.25rem', fontSize: '1.1rem'}}>Publishing</h3>
              
              {/* Featured Toggle */}
              <div 
                onClick={() => setFormData(prev => ({...prev, is_featured: prev.is_featured == 1 ? 0 : 1}))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.8rem', marginBottom: '1.5rem', borderRadius: '0.5rem',
                  border: `1px solid ${formData.is_featured == 1 ? '#fbbf24' : '#e5e7eb'}`,
                  backgroundColor: formData.is_featured == 1 ? '#fffbeb' : '#f9fafb',
                  cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                  <div style={{
                    padding: '6px', borderRadius: '50%', 
                    backgroundColor: formData.is_featured == 1 ? '#fbbf24' : '#e5e7eb',
                    color: formData.is_featured == 1 ? '#fff' : '#9ca3af'
                  }}>
                    <Star size={16} fill={formData.is_featured == 1 ? "currentColor" : "none"} />
                  </div>
                  <span style={{fontWeight: '600', color: '#374151', fontSize: '0.9rem'}}>Featured Article</span>
                </div>
                <div style={{
                  width: '36px', height: '20px', backgroundColor: formData.is_featured == 1 ? '#fbbf24' : '#d1d5db',
                  borderRadius: '20px', position: 'relative', transition: 'background-color 0.2s'
                }}>
                  <div style={{
                    width: '16px', height: '16px', backgroundColor: '#fff', borderRadius: '50%',
                    position: 'absolute', top: '2px', left: formData.is_featured == 1 ? '18px' : '2px',
                    transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }} />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Status Publikasi</label>
                <select style={styles.input} name="status" value={formData.status} onChange={handleChange}>
                  <option value="draft">Draft (Disimpan Saja)</option>
                  <option value="published">Published (Tampilkan)</option>
                </select>
              </div>
              <button 
                onClick={handleSubmit}
                disabled={submitting}
                style={styles.saveBtn}
              >
                {submitting ? <Loader2 className="animate-spin" size={20}/> : <Save size={20}/>}
                {submitting ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>

            {/* THUMBNAIL CARD */}
            <div style={styles.card}>
              <h3 style={{fontWeight: '700', marginBottom: '1rem', fontSize: '1.1rem'}}>Cover Image</h3>
              <div style={styles.inputGroup}>
                <div 
                  onClick={handleCoverImageSelect}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '2rem 1rem', border: '2px dashed #d1d5db', borderRadius: '0.75rem', cursor: 'pointer', color: '#6b7280',
                    transition: 'all 0.2s', backgroundColor: '#f9fafb'
                  }}>
                  <ImageIcon size={32} strokeWidth={1.5} />
                  <span style={{fontSize: '0.9rem', marginTop: '0.75rem', fontWeight: '500'}}>
                     {imagePreview ? 'Ganti Gambar' : 'Pilih / Upload Gambar'}
                  </span>
                </div>
                {imagePreview && (
                  <div style={{marginTop: '1rem'}}>
                    <img src={imagePreview} alt="Preview" style={styles.previewImg} />
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* BOTTOM SECTION: SEO & PREVIEW (Moved here) */}
        <div style={styles.bottomSection}>
            {/* SEO CARD */}
            <div style={styles.card}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{fontWeight: '700', fontSize: '1.1rem', margin: 0}}>SEO Score</h3>
                <div style={styles.scoreCircle(seoStats.score)}>
                  {seoStats.score}
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}><Search size={16}/> Fokus Keyword</label>
                <input 
                  style={styles.input} 
                  name="seo_focus_keyword" value={formData.seo_focus_keyword} onChange={handleChange} 
                  placeholder="e.g. Konveksi Bandung"
                />
              </div>

              <div style={{fontSize: '0.85rem', color: '#4b5563', display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingBottom: '1rem', borderBottom: '1px solid #f3f4f6'}}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Jumlah Kata</span> <b>{seoStats.words}</b></div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Kepadatan Keyword</span> <b>{seoStats.density}%</b></div>
              </div>

              {seoStats.issues.length > 0 ? (
                <div style={{marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', fontSize: '0.85rem'}}>
                  <div style={{fontWeight:'600', color:'#991b1b', marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:'0.4rem'}}>
                    <AlertCircle size={14}/> Perbaiki:
                  </div>
                  <ul style={{paddingLeft:'1rem', margin:0, color:'#b91c1c', lineHeight: '1.4'}}>
                    {seoStats.issues.map((issue, i) => <li key={i} style={{marginBottom:'0.3rem'}}>{issue}</li>)}
                  </ul>
                </div>
              ) : (
                 <div style={{marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', fontSize: '0.85rem', color: '#166534', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}>
                   <CheckCircle2 size={16}/> Artikel sudah optimal!
                 </div>
              )}
            </div>

            {/* GOOGLE PREVIEW */}
            <div style={styles.card}>
              <h3 style={{fontWeight: '700', marginBottom: '1rem', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <Eye size={18} /> Google Preview
              </h3>
              
              <div style={{marginBottom: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', backgroundColor: '#fff'}}>
                <div style={{fontSize: '0.85rem', color: '#4b5563'}}>imoonusel.com › ...</div>
                <div style={{fontSize: '1.15rem', color: '#1a0dab', fontWeight: '400', lineHeight: '1.3', marginBottom: '0.25rem', marginTop: '0.25rem'}}>
                  {formData.meta_title_id || formData.title_id || 'Judul Artikel'}
                </div>
                <div style={{fontSize: '0.9rem', color: '#4d5156', lineHeight: '1.5'}}>
                  {formData.meta_description_id || 'Deskripsi artikel yang akan muncul di hasil pencarian...'}
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Meta Title</label>
                <input style={styles.input} name="meta_title_id" value={formData.meta_title_id} onChange={handleChange} placeholder="Judul Khusus SEO" />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Meta Description</label>
                <textarea style={styles.textarea} name="meta_description_id" value={formData.meta_description_id} onChange={handleChange} placeholder="Ringkasan menarik untuk pencari..." />
              </div>
            </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ArticleEditor;
